import {
  FC,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  Screen,
  ScreenHeaderWithInbox,
  Feed,
} from '@app/components'
import { FeedFiltersModal } from '@app/components/Feed/FeedMenu/components/FeedFiltersModal'
import {
  Ads,
  Sports,
  ChannelContent,
  Placement,
  UserRoles,
  useGetAdvertisementLazyQuery,
  useGetMyFavoriteBookieQuery,
  useGetPrivateChannelDataLazyQuery,
  usePrivateChannelContentLazyQuery,
} from '@app/graphql'
import {
  PublicChannelState,
  CurrentUserState,
  showSignupPromptModal,
  CurrentCountryState,
  PrivateChannelState,
  guestMode,
} from '@app/storage'
import { useSnapshot } from 'valtio'
import styled from 'styled-components'
import { getGraphQLFilterVariables, getActiveFilterCount } from '@app/components/Feed/FeedMenu/state'
import { getExcludedAdIds, syncAdConfigs } from '@app/helpers/adFrequency'
import { Icon, IconColor, IconTypes } from '@app/ui'
import { useInstallPrompt } from '@app/hooks/useInstallPrompt'
import { INSTALL_PROMPT_STORAGE_KEY } from '@app/constants'
import { themeManager } from '@app/helpers/theme'
import { useFavoriteBookieEnabled } from '@app/hooks/useFavoriteBookieEnabled'
import FavoriteBookieModal from '@app/ui/FavoriteBookieModal'

interface PublicChannelScreenProps {}


const PublicChannel: FC<PublicChannelScreenProps> = () => {
  const limit = 10

  const { t } = useTranslation()
  const screenUpRef = useRef(null)
  const {
    isPublic,
    offset,
    hasMoreData,
    channelData,
    adsData,
    sports,
    pinnedContent,
    prevOffset
  } = useSnapshot(PublicChannelState)
  const { countryId, currentChannelId: channelId, favoriteTenantBookieId, userDataLoading } = useSnapshot(CurrentUserState)
  const { enabled: favoriteBookieEnabled, loading: favoriteBookieFlagLoading } = useFavoriteBookieEnabled()
  const { data: myFavoriteBookieData } = useGetMyFavoriteBookieQuery({
    skip: userDataLoading || favoriteBookieFlagLoading || !favoriteBookieEnabled || guestMode(),
    fetchPolicy: 'network-only',
  })

  const { id: currentCountryId } = useSnapshot(CurrentCountryState)

  const [ scrolledUp, setScrolledUp ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const [ loadingMore, setLoadingMore ] = useState(false)
  const [ loadError, setLoadError ] = useState(false)
  const [ openTipsCreation, setOpenTipsCreation ] = useState(false)
  const [ openPostCreation, setOpenPostCreation ] = useState(false)
  const [ openPollCreation, setOpenPollCreation ] = useState(false)
  const [ showFilterModal, setShowFilterModal ] = useState(false)
  const [ activeFilterCount, setActiveFilterCount ] = useState(0)
  const [ showFavoriteBookieModal, setShowFavoriteBookieModal ] = useState(false)

  const reloadChannelContent = async () => {
    try {
      setLoading(true)
      const response = await getPrivateChannelContent({
        variables: {
          channelId,
          limit,
          cursor: null,
          filter: {
            ...getGraphQLFilterVariables()
          }
        },
      })
      const { privateChannelContent } = response.data ?? {}
      const { feed, nextCursor, previousCursor } = privateChannelContent
      saveScrollPosition()
      PublicChannelState.channelData = feed as ChannelContent[]
      PublicChannelState.offset = nextCursor
      PublicChannelState.prevOffset = previousCursor
      PublicChannelState.hasMoreData = !!nextCursor
      PrivateChannelState.id = channelId
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterModalSubmit = async () => {
    setShowFilterModal(false)
    setActiveFilterCount(getActiveFilterCount())
    await reloadChannelContent()
  }

  const [ getPrivateChannelData ] = useGetPrivateChannelDataLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const [ getAds ] = useGetAdvertisementLazyQuery({
    fetchPolicy: 'no-cache',
  })
  const [ getPrivateChannelContent ] = usePrivateChannelContentLazyQuery({
    fetchPolicy: 'no-cache',
  })

  useLayoutEffect(() => {
    if (!scrolledUp && screenUpRef?.current) {
      screenUpRef.current.scrollIntoView({ block: 'start' })
      setScrolledUp(true)
    }
  }, [screenUpRef?.current, scrolledUp])

  useEffect(() => {
    if (!isPublic) PublicChannelState.isPublic = true

    initialLoad()
  }, [])

  useEffect(() => {
    if (userDataLoading || favoriteBookieFlagLoading || guestMode()) return
    if (!favoriteBookieEnabled || !myFavoriteBookieData) return
    const savedTenantBookieId = myFavoriteBookieData.myFavoriteBookie?.tenantBookieId ?? null
    if (savedTenantBookieId && favoriteTenantBookieId !== savedTenantBookieId) {
      CurrentUserState.favoriteTenantBookieId = savedTenantBookieId
    }
    setShowFavoriteBookieModal(!savedTenantBookieId && favoriteTenantBookieId == null)
  }, [
    userDataLoading,
    favoriteTenantBookieId,
    favoriteBookieEnabled,
    favoriteBookieFlagLoading,
    myFavoriteBookieData,
  ])
  const initialLoad = async () => {
    setLoading(true)
    const adFilter = {
      isActive: true,
      channelIds: [ channelId ],
      countryIds: [ currentCountryId || countryId ],
    }
    try {
      const [ channelRes, adsRes ] = await Promise.allSettled([
        getPrivateChannelData({
          variables: { limit, channelId, cursor: null },
        }),
        getAds({
          variables: { channelId, placement: Placement.Feed, excludeIds: getExcludedAdIds(), filter: adFilter },
        }),
      ])

      const adsByPlacement = adsRes.status === 'fulfilled'
        ? adsRes.value?.data?.adsByPlacement ?? []
        : []

      syncAdConfigs(adsByPlacement.filter((a) => !!a.id).map((a) => ({
        id: a.id,
        frequencyCapImpressions: a.frequencyCapImpressions ?? null,
        frequencyCapWindow: a.frequencyCapWindow ?? null,
      })))

      const privateChannelContent =
        channelRes.status === 'fulfilled'
          ? channelRes.value?.data?.privateChannelContent ?? {}
          : {}
      const channelDetailed =
        channelRes.status === 'fulfilled'
          ? channelRes.value?.data?.channelDetailed ?? {}
          : {}
      const feed = privateChannelContent?.feed ?? []
      const nextCursor = privateChannelContent?.nextCursor ?? null
      const previousCursor = privateChannelContent?.previousCursor ?? null
      const pinnedContent = channelDetailed?.pinnedContent ?? null
      Object.assign(PublicChannelState, {
        channelData: feed as ChannelContent[],
        offset: nextCursor,
        hasMoreData: Boolean(nextCursor),
        pinnedContent,
        adsData: adsByPlacement,
        prevOffset: previousCursor,
      })
      PrivateChannelState.id = channelId
    } catch (error) {
      console.error('❌ Initial load failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const getInitialData = async () => {
    try {
      setLoading(true)
      const response = await getPrivateChannelContent({
        variables: {
          channelId,
          limit,
          cursor: null,
          filter: {
            ...getGraphQLFilterVariables()
          }
        },
      })
      const { privateChannelContent } = response.data ?? {}
      const { feed, nextCursor, previousCursor } = privateChannelContent
      PublicChannelState.channelData = feed as ChannelContent[]
      PublicChannelState.offset = nextCursor
      PublicChannelState.prevOffset = previousCursor
      PublicChannelState.hasMoreData = !!nextCursor
      PrivateChannelState.id = channelId
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const getNextData = async () => {
    try {
      setLoadingMore(true)
      if(!offset) return
      PublicChannelState.prevOffset = prevOffset
      const response = await getPrivateChannelContent({
        variables: {
          channelId,
          limit,
          filter: {
            ...getGraphQLFilterVariables()
          },
          cursor:  {
            id: offset.id,
            createdAt: offset.createdAt
          } },
      })
      const { privateChannelContent } = response.data ?? {}
      const { feed, nextCursor, previousCursor } = privateChannelContent
      if (!response?.data || response.error) {
        setLoadError(true)
      } else if (!feed?.length) {
        PublicChannelState.hasMoreData = false
      } else {
        PublicChannelState.offset = nextCursor
        PublicChannelState.prevOffset = previousCursor
        saveScrollPosition()
        PublicChannelState.channelData = [
          ...PublicChannelState.channelData,
          ...feed,
        ] as ChannelContent[]
        PublicChannelState.hasMoreData = !!nextCursor
        PrivateChannelState.id = channelId

        const currentAdsData = PublicChannelState.adsData
        const adConfigs = currentAdsData.filter((a) => !!a.id).map((a) => ({
          id: a.id,
          frequencyCapImpressions: a.frequencyCapImpressions ?? null,
          frequencyCapWindow: a.frequencyCapWindow ?? null,
          rotationWeight: a.rotationWeight ?? 5,
        }))
        const excludeIds = getExcludedAdIds(adConfigs)

        const adsResult = await getAds({
          variables: {
            channelId,
            placement: Placement.Feed,
            excludeIds,
            filter: {
              isActive: true,
              channelIds: [ channelId ],
              countryIds: [ currentCountryId || countryId ],
            },
          },
        })
        const freshAds = (adsResult.data?.adsByPlacement ?? []).filter(
          (a) => !currentAdsData.some((existing) => existing.id === a.id)
        )
        if (freshAds.length) {
          PublicChannelState.adsData = [ ...currentAdsData, ...freshAds ]
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingMore(false)
    }
  }

  const saveScrollPosition = () => {
    PublicChannelState.scrollPosition = window.scrollY
  }

  const propmtSignupModal = showSignupPromptModal({
    message: t('incognito.modal.loginDefaultMessage'),
    title: t('incognito.modal.loginTitle'),
  })
  useInstallPrompt(INSTALL_PROMPT_STORAGE_KEY)

  return (
    <Screen
      title={t('common.feed')}
      withNavigation
      noPadding
      noFixedHeight
      tipsLoading={loading}
    >
      <div ref={screenUpRef} />
      <ScreenHeaderWithInbox
        rightIcon={
          <FilterIconWrapper>
            <Icon type={IconTypes.filterIcon} color={themeManager.isLight() ? IconColor.textPrimary : IconColor.white} width={20} height={20} />
            {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
          </FilterIconWrapper>
        }
        showMenuButton
        bottomGap={12}
        onRightIconClick={() => setShowFilterModal(true)}
      />

      {showFilterModal && <FeedFiltersModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onSubmit={handleFilterModalSubmit}
      />}

      {showFavoriteBookieModal && <FavoriteBookieModal
        open={showFavoriteBookieModal}
        onClose={() => setShowFavoriteBookieModal(false)}
        onSaved={reloadChannelContent}
      />}

      <Container pinnedContent={Boolean(pinnedContent)}>
        <Feed
          channelData={ channelData as ChannelContent[]}
          onUpdate={guestMode() ? propmtSignupModal : getInitialData}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMoreData}
          loadContent={getNextData}
          adsData={adsData as Partial<Ads>[]}
          isAnonimized={true}
          loadError={loadError}
          pinContentAccess={[ UserRoles.Admin ]}
          createContentAccess={[ UserRoles.Admin ]}
          sportData={sports as Sports[]}
          setHideChannelList={() => null}
          setOpenTipsCreation={setOpenTipsCreation}
          openPostCreation={openPostCreation}
          setOpenPostCreation={setOpenPostCreation}
          openPollCreation={openPollCreation}
          setOpenPollCreation={setOpenPollCreation}
          openTipsCreation={openTipsCreation}
        />
      </Container>
    </Screen>
  )
}

const Container = styled.div<{ pinnedContent: boolean }>`
  margin-top: 118px;
  min-height: calc(var(--app-height) - 144px);
`

const FilterIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`

const FilterBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: var(--bg-quinticenmary);
  color: var(--text-primary);
  font-size: 10px;
  font-weight: 700;
  min-width: 14px;
  height: 14px;
  border-radius: 8px;
  display: flex;
  font-weight: 900;
  align-items: center;
  justify-content: center;
  padding: 6px;
`

export default PublicChannel
